import { useContext, useEffect } from "react";

import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

import useAxios from "@hooks/use-axios";
import { UserContext } from "@context/user";
import { BRAND_ENDPOINTS } from "@constants/endpoints";
import { PAYMENT_MODEL } from "@constants/panel/common";
import useBrandRouteGuard from "../../use-brand-route-guard";
import { brandSetupDefaults, brandSetupSchema } from "../misc/schema";

// The one brand route that is allowed to render while setup is pending — every
// other one bounces here (see use-brand-route-guard). It bounces back out again
// once the brand has answered, so a finished brand can't sit on the gate.
const useBrandSetup = () => {
    const { brandId, isValid, setupPending } = useBrandRouteGuard({ allowPendingSetup: true });
    const { brands, brandsLoaded, refreshBrands } = useContext(UserContext);
    const { axiosInstance, loading } = useAxios({ base: "brand" });
    const navigate = useNavigate();

    const brand = brands.find((item) => item.id === brandId);

    useEffect(() => {
        if (!brandsLoaded || !isValid) return;
        if (!setupPending) navigate(`/brand/${brandId}/dashboard`, { replace: true });
    }, [brandsLoaded, isValid, setupPending, brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const methods = useForm({
        resolver     : yupResolver(brandSetupSchema),
        defaultValues: brandSetupDefaults,
        mode         : "onChange"
    });

    // A self-payout brand pays its publishers directly, so no debit note is ever
    // raised against it — the question is hidden, and anything already picked
    // under managed payout is dropped so it can't be submitted by accident.
    const paymentModel = useWatch({ control: methods.control, name: "payment_model" });
    const showDebitNoteFrequency = paymentModel === PAYMENT_MODEL.MANAGED_PAYOUT;

    useEffect(() => {
        if (!showDebitNoteFrequency) methods.resetField("debit_note_frequency");
    }, [showDebitNoteFrequency]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = methods.handleSubmit(async (data) => {
        await axiosInstance.patch(BRAND_ENDPOINTS.PAYMENT_SETUP(brandId), {
            payment_model: data.payment_model,
            kyc_window   : Number(data.kyc_window),
            ...(showDebitNoteFrequency && { debit_note_frequency: data.debit_note_frequency })
        });
        // Re-read the accounts list so the gate lifts without a full reload.
        await refreshBrands();
        navigate(`/brand/${brandId}/dashboard`, { replace: true });
    });

    return { brand, brandsLoaded, methods, onSubmit, loading, showDebitNoteFrequency };
};

export default useBrandSetup;
